import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "operator", "value", "valueContainer", "hiddenBooleanValue", "dayCheckboxes"]

  static BOOLEAN_FIELDS = [
    'property_acquisition?', 'conversation.user_replied?', 'conversation.engaged_by_pipeline?',
    'from_massive_engagement?', 'listing.development',
    'user_sent_first_message?', 'human_requested?', 'forced_qualification', 'high_priority',
    'unknown_answer', 'manual_qualification', 'created_when_unavailable', 'contract_active?',
    'sponsored_ad?'
  ]

  static DAY_OF_WEEK_OPTIONS = [
    { value: 'segunda', label: 'Segunda' },
    { value: 'terça', label: 'Terça' },
    { value: 'quarta', label: 'Quarta' },
    { value: 'quinta', label: 'Quinta' },
    { value: 'sexta', label: 'Sexta' },
    { value: 'sábado', label: 'Sábado' },
    { value: 'domingo', label: 'Domingo' }
  ]

  static PRESENCE_FIELDS = ['visit_date']

  static OFFER_TYPE_OPTIONS = [
    { value: 'broker_partnership', label: 'Corretor propondo parceria' },
    { value: 'service_provider', label: 'Prestador de serviço' },
    { value: 'job_seeker', label: 'Candidato a vaga de emprego' },
    { value: 'listing_removal', label: 'Pedido de retirada de anúncio' }
  ]

  fieldChanged() {
    const selectedField = this.fieldTarget.value
    const isBooleanField = this.constructor.BOOLEAN_FIELDS.includes(selectedField)
    const isDayOfWeek = selectedField === 'current_day_of_week'
    const isHour = selectedField === 'current_hour'
    const isPresenceField = this.constructor.PRESENCE_FIELDS.includes(selectedField)

    if (isDayOfWeek) {
      this.operatorTarget.innerHTML = this._buildDayOfWeekOperators()
      this._rebuildDayOfWeekInput()
    } else if (isHour) {
      this.operatorTarget.innerHTML = this._buildHourOperators()
      this._rebuildHourInput()
    } else if (isBooleanField) {
      this.operatorTarget.innerHTML = this._buildBooleanOperators()
      this._rebuildValueInput(true)
    } else if (selectedField === 'offer_type') {
      this.operatorTarget.innerHTML = this._buildOfferTypeOperators()
      this._rebuildOfferTypeValueInput()
    } else if (isPresenceField) {
      this.operatorTarget.innerHTML = this._buildPresenceFirstOperators()
      this._rebuildValueInput(false)
    } else {
      this.operatorTarget.innerHTML = this._buildStandardOperators()
      this._rebuildValueInput(false)
    }
  }

  operatorChanged() {
    const operator = this.operatorTarget.value
    const noValueOperators = ['present', 'blank']
    const needsValue = !noValueOperators.includes(operator)

    if (this.hasValueContainerTarget) {
      this.valueContainerTarget.classList.toggle('hidden', !needsValue)
    }
  }

  dayCheckboxChanged(event) {
    const label = event.target.closest('label')
    if (!label) return

    const checked = event.target.checked
    label.className = 'inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium cursor-pointer transition-all ' +
      (checked ? 'bg-yellow-50 border-yellow-400 text-yellow-800' : 'border-gray-200 text-gray-500 hover:border-gray-300')
  }

  selectBoolean(event) {
    const selectedValue = event.currentTarget.dataset.value

    if (this.hasHiddenBooleanValueTarget) {
      this.hiddenBooleanValueTarget.value = selectedValue
    }

    const buttons = this.valueContainerTarget.querySelectorAll('button[data-value]')
    buttons.forEach(btn => {
      const isTrue = btn.dataset.value === 'true'
      const isSelected = btn.dataset.value === selectedValue

      btn.className = 'flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all'
      if (isSelected && isTrue) {
        btn.classList.add('bg-green-50', 'border-green-400', 'text-green-700')
      } else if (isSelected && !isTrue) {
        btn.classList.add('bg-red-50', 'border-red-400', 'text-red-700')
      } else {
        btn.classList.add('border-gray-200', 'text-gray-500', 'hover:border-gray-300')
      }
    })
  }

  // --- Private ---

  _rebuildValueInput(isBooleanField) {
    if (!this.hasValueContainerTarget) return

    const container = this.valueContainerTarget
    container.classList.remove('hidden')

    const existingInput = container.querySelector('input[name], select[name]')
    const inputName = existingInput ? existingInput.name : this._inferInputName()

    if (isBooleanField) {
      container.innerHTML = `
        <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Valor</span></label>
        <div class="flex gap-2">
          <button type="button"
                  class="flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all bg-green-50 border-green-400 text-green-700"
                  data-action="click->lead-transformer-condition#selectBoolean"
                  data-value="true">Sim</button>
          <button type="button"
                  class="flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all border-gray-200 text-gray-500 hover:border-gray-300"
                  data-action="click->lead-transformer-condition#selectBoolean"
                  data-value="false">Não</button>
        </div>
        <input type="hidden" name="${inputName}" value="true"
               data-lead-transformer-condition-target="hiddenBooleanValue" />
      `
    } else {
      container.innerHTML = `
        <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Valor</span></label>
        <input type="text" name="${inputName}"
               class="input input-bordered w-full"
               placeholder="valor de comparação"
               data-lead-transformer-condition-target="value" />
      `
    }
  }

  _rebuildOfferTypeValueInput() {
    if (!this.hasValueContainerTarget) return

    const container = this.valueContainerTarget
    container.classList.remove('hidden')

    const existingInput = container.querySelector('input[name], select[name]')
    const inputName = existingInput ? existingInput.name : this._inferInputName()

    const options = this.constructor.OFFER_TYPE_OPTIONS
      .map(opt => `<option value="${opt.value}">${opt.label}</option>`)
      .join('')

    container.innerHTML = `
      <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Valor</span></label>
      <select name="${inputName}"
              class="select select-bordered w-full"
              data-lead-transformer-condition-target="value">
        <option value="">Selecione...</option>
        ${options}
      </select>
    `
  }

  _rebuildDayOfWeekInput() {
    if (!this.hasValueContainerTarget) return

    const container = this.valueContainerTarget
    container.classList.remove('hidden')

    const baseName = this._inferInputName().replace('[value]', '[values][]')
    const days = this.constructor.DAY_OF_WEEK_OPTIONS

    const checkboxes = days.map(day =>
      `<label class="inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium cursor-pointer transition-all border-gray-200 text-gray-500 hover:border-gray-300">
        <input type="checkbox" name="${baseName}" value="${day.value}" class="hidden"
               data-action="change->lead-transformer-condition#dayCheckboxChanged" />
        ${day.label}
      </label>`
    ).join('')

    container.innerHTML = `
      <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Dias</span></label>
      <div class="flex flex-wrap gap-1.5" data-lead-transformer-condition-target="dayCheckboxes">
        ${checkboxes}
      </div>
    `
  }

  _rebuildHourInput() {
    if (!this.hasValueContainerTarget) return

    const container = this.valueContainerTarget
    container.classList.remove('hidden')

    const inputName = this._inferInputName()

    container.innerHTML = `
      <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Hora (0-23)</span></label>
      <input type="number" name="${inputName}" min="0" max="23" step="1"
             class="input input-bordered w-full"
             placeholder="Ex: 10"
             data-lead-transformer-condition-target="value" />
      <p class="mt-1 text-xs text-gray-400">Hora cheia (0 = meia-noite, 14 = 14h, 23 = 23h)</p>
    `
  }

  _inferInputName() {
    if (this.hasFieldTarget) {
      return this.fieldTarget.name.replace('[field]', '[value]')
    }
    return 'lead_transformer[conditions][0][value]'
  }

  _buildDayOfWeekOperators() {
    return `
      <option value="in">Está nos dias selecionados</option>
      <option value="not_in">Não está nos dias selecionados</option>
    `
  }

  _buildHourOperators() {
    return `
      <option value="">Selecione um operador</option>
      <option value="equals">= É igual a</option>
      <option value="not_equals">≠ É diferente de</option>
      <option value="greater_than">&gt; Maior que</option>
      <option value="greater_than_or_equal">&gt;= Maior ou igual a</option>
      <option value="less_than">&lt; Menor que</option>
      <option value="less_than_or_equal">&lt;= Menor ou igual a</option>
    `
  }

  _buildBooleanOperators() {
    return `
      <option value="equals">É Igual a</option>
      <option value="not_equals">Não é igual a</option>
    `
  }

  _buildOfferTypeOperators() {
    return `
      <option value="">Selecione um operador</option>
      <optgroup label="Cliente ou não">
        <option value="blank">É cliente comum (buscando imóvel)</option>
        <option value="present">Não é cliente (parceria, prestador, vaga ou retirada de anúncio)</option>
      </optgroup>
      <optgroup label="Tipo específico">
        <option value="equals">= É exatamente</option>
        <option value="not_equals">≠ É qualquer um, exceto</option>
      </optgroup>
    `
  }

  _buildPresenceFirstOperators() {
    return `
      <option value="">Selecione um operador</option>
      <optgroup label="Presença">
        <option value="present">Tem valor</option>
        <option value="blank">Está vazio</option>
      </optgroup>
      <optgroup label="Comparação">
        <option value="equals">= É igual a</option>
        <option value="not_equals">≠ É diferente de</option>
      </optgroup>
      <optgroup label="Texto">
        <option value="contains">Contém</option>
        <option value="not_contains">Não contém</option>
        <option value="starts_with">Começa com</option>
        <option value="ends_with">Termina com</option>
      </optgroup>
    `
  }

  _buildStandardOperators() {
    return `
      <option value="">Selecione um operador</option>
      <optgroup label="Comparação">
        <option value="equals">= É igual a</option>
        <option value="not_equals">≠ É diferente de</option>
      </optgroup>
      <optgroup label="Texto">
        <option value="contains">Contém</option>
        <option value="not_contains">Não contém</option>
        <option value="starts_with">Começa com</option>
        <option value="ends_with">Termina com</option>
      </optgroup>
      <optgroup label="Presença">
        <option value="present">Tem valor</option>
        <option value="blank">Está vazio</option>
      </optgroup>
      <optgroup label="Número">
        <option value="greater_than">&gt; Maior que</option>
        <option value="greater_than_or_equal">&gt;= Maior ou igual a</option>
        <option value="less_than">&lt; Menor que</option>
        <option value="less_than_or_equal">&lt;= Menor ou igual a</option>
      </optgroup>
    `
  }
}
